/* The app's one tabular-file reader: CSV in, CSV out, xlsx in.
 *
 * It replaces three copies of `reader.result.split(/\r?\n/)` + `row.split(",")` — one in each of
 * the old import tabs — plus a fourth hand-rolled `join(",")` writer in the upload log. That
 * approach breaks on the first field containing a comma, which for a task *description* is not an
 * edge case, and it silently produced garbage for the `.xlsx` files the assortment-matrix tab
 * advertised it accepted.
 *
 * `scripts/check-csv.mjs` is the runnable check: `yarn check:csv`.
 */

/**
 * RFC 4180, as a single character scan.
 *
 * Handles quoted fields (so a value may contain a comma or a newline), `""` as an escaped quote,
 * CRLF / LF / lone-CR line endings, and a UTF-8 BOM — which Excel writes and which otherwise ends
 * up *inside* the first header cell, so a column named `id` never matches.
 *
 * Line endings are normalised up front rather than branched on in the loop: a CRLF inside a quoted
 * field should become a newline anyway, so there is nothing to preserve.
 *
 * @param {string} text
 * @returns {string[][]} rows of raw cell strings; wholly blank rows are dropped
 */
export function parseCsv(text) {
  const src = String(text || "")
    .replace(/^\uFEFF/, "")
    .replace(/\r\n?/g, "\n");

  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;

  for (let i = 0; i < src.length; i++) {
    const char = src[i];

    if (quoted) {
      if (char !== '"') {
        field += char;
      } else if (src[i + 1] === '"') {
        field += '"';
        i++;
      } else {
        quoted = false;
      }
      continue;
    }

    if (char === '"') quoted = true;
    else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += char;
  }

  row.push(field);
  rows.push(row);

  /* A trailing newline leaves one empty row, and files exported from a spreadsheet routinely carry
     a run of them. A row that is blank in every cell carries no record either way. */
  return rows.filter(cells => cells.some(cell => cell.trim() !== ""));
}

/** Quote only where the value forces it, so a plain file stays diffable and readable. */
const escapeCell = value => {
  const cell = value == null ? "" : String(value);
  return /["\n,]/.test(cell) ? `"${cell.split('"').join('""')}"` : cell;
};

/**
 * Rows out. Used for the downloadable per-kind template and for the error-log export, which the
 * old page built with `join(",")` — so any failing task whose title held a comma exported a row
 * with the columns shifted by one.
 *
 * @param {Array<Array<unknown>>} rows
 * @returns {string}
 */
export const stringifyCsv = rows => rows.map(cells => cells.map(escapeCell).join(",")).join("\n");

/**
 * Read a dropped file into rows, dispatching on its extension.
 *
 * SheetJS is loaded **dynamically**, so the ~300 KB reader is fetched only once someone actually
 * drops a spreadsheet — a CSV import pays nothing for it. It is installed from SheetJS's own
 * registry rather than npm: npm's last published `xlsx` is 0.18.5, which carries CVE-2023-30533
 * (prototype pollution) and a ReDoS, and the fixes were never published there.
 *
 * `cellDates` + `raw: false` are what make a date cell arrive as `31/12/2026` rather than the
 * serial number `46387`, so both paths hand the same shape to the same validator.
 *
 * @param {File} file
 * @returns {Promise<string[][]>}
 */
export async function readTable(file) {
  const name = (file?.name || "").toLowerCase();

  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const XLSX = await import("xlsx");
    const book = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
    /* The first sheet only. A workbook whose data is on sheet 2 is a support question, not a
       silently-half-imported batch. */
    const sheet = book.Sheets[book.SheetNames[0]];
    if (!sheet) return [];

    return XLSX.utils
      .sheet_to_json(sheet, { header: 1, blankrows: false, defval: "", raw: false, dateNF: "dd/mm/yyyy" })
      .map(cells => cells.map(cell => (cell == null ? "" : String(cell))))
      .filter(cells => cells.some(cell => cell.trim() !== ""));
  }

  return parseCsv(await file.text());
}

/** A `Blob` download, shared by the template button and the error-log export. */
export function downloadCsv(rows, filename) {
  const url = URL.createObjectURL(new Blob(["\uFEFF" + stringifyCsv(rows)], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  /* Revoking synchronously cancels the download in Safari. */
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
